import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { siteData } from '../data.js';
import SEO from '../components/SEO.jsx';
import { useCmsCollection } from '../lib/cms.js';
import { Calendar, User, Tag, ArrowRight, FileText, Download, X, Search, Filter } from 'lucide-react';

export default function News() {
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: rawNews } = useCmsCollection('nyheder', siteData.news || []);

  const newsArticles = rawNews.map((art, idx) => {
    const d = art.data || art;
    return {
      id: d.id || art.id || `news-${idx}`,
      title: d.titel || d.title,
      author: d.forfatter || d.author || 'TFDI',
      date: d.dato || d.date,
      category: d.kategori || d.category || 'Nyheder',
      excerpt: d.uddrag || d.excerpt,
      content: d.indhold || d.content,
      image: Array.isArray(d.billede) ? d.billede[0] : (d.billede || d.image),
      featured: d.fremhaevet !== undefined ? d.fremhaevet : Boolean(d.featured),
    };
  });

  // Categories for filter
  const categories = ['Alle', 'Nyheder', 'Behandling'];

  // Filter articles based on category and search query
  const filteredArticles = newsArticles.filter(article => {
    const matchesCategory = activeCategory === 'Alle' || article.category === activeCategory;
    const matchesSearch = (article.title && article.title.toLowerCase().includes(searchQuery.toLowerCase())) || 
                          (article.content && article.content.toLowerCase().includes(searchQuery.toLowerCase())) ||
                          (article.author && article.author.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Find the featured article (always "Ordinær generalforsamling" or marked as featured)
  const featuredArticle = newsArticles.find(article => article.featured);
  
  // Remaining articles for the grid (excluding featured if we are on 'Alle' and no search query)
  const gridArticles = filteredArticles.filter(article => {
    if (activeCategory === 'Alle' && !searchQuery) {
      return !article.featured;
    }
    return true;
  });

  const documents = [
    {
      title: "Indsamlingsregnskab 2024",
      description: "Det underskrevne regnskab for foreningens indsamlingsaktiviteter.",
      url: "https://assets.auxodev.dk/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/f8816f27-beb4-45f9-97de-7d32885688d0.pdf",
      fileName: "Indsamlingsregnskab-SIGNED.pdf"
    }
  ];

  return (
    <div className="bg-slate-50 min-h-screen pt-32 pb-24">
      <SEO
        title="Nyheder | Seneste nyt og artikler fra TFDI"
        description="Læs de seneste nyheder, tiltag og beretninger fra Tak For Din Indsats. Følg vores arbejde for Danmarks veteraner og frontpersonale."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Navigation Tabs (Seamless connection between News and Events) */}
        <div className="flex justify-center mb-12">
          <div className="bg-white p-1.5 rounded-2xl shadow-sm border border-slate-100 flex gap-1">
            <span className="px-6 py-2.5 rounded-xl text-sm font-bold bg-red-600 text-white shadow-md shadow-red-600/10">
              Nyheder
            </span>
            <Link 
              to="/nyheder/events" 
              className="px-6 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:text-slate-900 transition-all"
            >
              Events
            </Link>
          </div>
        </div>

        {/* Header Section */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-red-600 font-bold tracking-wider uppercase text-sm bg-red-50 px-4 py-1.5 rounded-full">
            Aktuelt fra foreningen
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 mt-4 mb-6 tracking-tight">
            Nyheder / Events
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
            Følg med i vores arbejde, politiske indsatser, arrangementer og nye behandlingsresultater for Danmarks frontpersonale.
          </p>
        </div>

        {/* Filter & Search Bar, Featured and Grid */}
        {newsArticles.length === 0 ? (
          <div className="space-y-8 max-w-3xl mx-auto">
            {/* Event highlight card */}
            <div className="bg-white rounded-[2.5rem] p-8 sm:p-10 border border-slate-200/80 shadow-xl shadow-slate-200/40 text-left relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full blur-3xl pointer-events-none"></div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="bg-red-600 text-white text-xs font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-xl">
                  Afholdt event
                </span>
                <span className="text-xs font-semibold text-slate-500">
                  10. september 2026 • Utzon Center, Aalborg
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-3">
                Velgørenhedsaften 10. september 2026
              </h2>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-6">
                Der blev 10. september afholdt TFDI velgørenhedsaften på Utzon Center i Aalborg. Se videoerne ”Video UM” og ”Musik” fra aftenen. Alle indkomne beløb går ubeskåret til foreningens aktiviteter.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/nyheder/events"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-xl shadow-md shadow-red-600/20 transition-colors"
                >
                  Gå til Events & se videoerne
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="bg-white/80 rounded-2xl p-8 text-center border border-slate-150">
              <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3 text-slate-500">
                <FileText className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1">Skriftlige nyheder opdateres snarest</h3>
              <p className="text-sm text-slate-500">
                Løbende nyhedsartikler og beretninger vil blive offentliggjort her på siden.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Filter & Search Bar */}
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 mb-12 flex flex-col md:flex-row gap-6 justify-between items-center">
              {/* Category Tabs */}
              <div className="flex flex-wrap gap-2 w-full md:w-auto">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-300 cursor-pointer ${
                      activeCategory === cat
                        ? 'bg-red-600 text-white shadow-lg shadow-red-600/20'
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Search Input */}
              <div className="relative w-full md:w-80">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Søg i artikler..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Featured Article Section (Only shown when category is 'Alle' and no search query) */}
            {activeCategory === 'Alle' && !searchQuery && featuredArticle && (
              <div className="mb-16">
                <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse"></span>
                  Fremhævet nyhed
                </h2>
                <div className="bg-white rounded-[2.5rem] overflow-hidden border border-slate-100 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-slate-200/60 transition-all duration-500 group">
                  <div className="grid grid-cols-1 lg:grid-cols-12">
                    {/* Image */}
                    <div className="lg:col-span-7 relative min-h-[300px] lg:min-h-[450px] overflow-hidden">
                      <img
                        src={featuredArticle.image}
                        alt={featuredArticle.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent"></div>
                      <span className="absolute top-6 left-6 bg-red-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-xl shadow-md">
                        {featuredArticle.category}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="lg:col-span-5 p-8 md:p-12 flex flex-col justify-between">
                      <div>
                        {/* Meta */}
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-6">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4 text-red-500" />
                            {featuredArticle.date}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <User className="w-4 h-4 text-red-500" />
                            Skrevet af {featuredArticle.author}
                          </span>
                        </div>

                        <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-4 leading-tight group-hover:text-red-600 transition-colors">
                          {featuredArticle.title}
                        </h3>
                        <p className="text-slate-600 leading-relaxed mb-8 text-base">
                          {featuredArticle.excerpt}
                        </p>
                      </div>

                      <div>
                        <button
                          onClick={() => setSelectedArticle(featuredArticle)}
                          className="inline-flex items-center gap-2 px-6 py-3.5 bg-slate-900 text-white rounded-2xl font-bold hover:bg-red-600 transition-all shadow-lg shadow-slate-900/10 hover:shadow-red-600/20 cursor-pointer group/btn"
                        >
                          Læs mere
                          <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Articles Grid */}
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-extrabold text-slate-900">
                  {activeCategory === 'Alle' ? 'Alle nyheder' : activeCategory}
                  <span className="ml-3 text-sm font-medium text-slate-400 bg-slate-200/60 px-2.5 py-1 rounded-full">
                    {filteredArticles.length}
                  </span>
                </h2>
              </div>

              {filteredArticles.length === 0 ? (
                <div className="bg-white rounded-3xl p-16 text-center border border-slate-150 max-w-xl mx-auto">
                  <p className="text-lg text-slate-500 mb-4">Ingen nyheder fundet, der matcher din søgning.</p>
                  <button 
                    onClick={() => { setSearchQuery(''); setActiveCategory('Alle'); }}
                    className="text-red-600 font-bold hover:underline"
                  >
                    Nulstil filtre
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {gridArticles.map((article, index) => (
                    <article 
                      key={index}
                      className="bg-white rounded-[2rem] overflow-hidden border border-slate-100 shadow-lg shadow-slate-200/40 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 flex flex-col h-full group"
                    >
                      {/* Image */}
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-slate-900 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg shadow-sm">
                          {article.category}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="p-6 flex flex-col justify-between flex-grow">
                        <div>
                          {/* Meta */}
                          <div className="flex items-center gap-4 text-xs text-slate-400 mb-4">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5 text-red-500" />
                              {article.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-3.5 h-3.5 text-red-500" />
                              {article.author}
                            </span>
                          </div>

                          <h3 className="text-xl font-bold text-slate-900 mb-3 leading-snug group-hover:text-red-600 transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-3">
                            {article.excerpt}
                          </p>
                        </div>

                        <div>
                          <button
                            onClick={() => setSelectedArticle(article)}
                            className="inline-flex items-center gap-1.5 text-sm font-bold text-red-600 hover:text-red-700 transition-colors group/link cursor-pointer"
                          >
                            Læs mere
                            <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

      </div>

      {/* Article Detail Modal */}
      {selectedArticle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div 
            className="bg-white rounded-[2.5rem] max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-200 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={() => setSelectedArticle(null)}
              className="absolute top-6 right-6 p-2 bg-white/90 backdrop-blur-sm rounded-full text-slate-700 hover:text-red-600 hover:scale-110 transition-all shadow-md z-10 cursor-pointer"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Header Image */}
            <div className="h-72 md:h-96 relative">
              <img 
                src={selectedArticle.image} 
                alt={selectedArticle.title} 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent"></div>
              <span className="absolute bottom-6 left-8 bg-red-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-xl shadow-md">
                {selectedArticle.category}
              </span>
            </div>

            {/* Content */}
            <div className="p-8 md:p-12 -mt-6 relative bg-white rounded-t-[2.5rem]">
              {/* Meta */}
              <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-6">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-red-500" />
                  {selectedArticle.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <User className="w-4 h-4 text-red-500" />
                  Skrevet af {selectedArticle.author}
                </span>
              </div>

              <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
                {selectedArticle.title}
              </h2>

              <div className="prose prose-slate max-w-none">
                <p className="text-lg text-slate-700 leading-relaxed mb-6 font-medium">
                  {selectedArticle.excerpt}
                </p>
                <p className="text-slate-600 leading-relaxed text-base">
                  {selectedArticle.content}
                </p>
              </div>

              <div className="mt-12 pt-8 border-t border-slate-100 flex justify-end">
                <button
                  onClick={() => setSelectedArticle(null)}
                  className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold rounded-xl transition-colors cursor-pointer"
                >
                  Luk vindue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
